import dayjs from "dayjs";
import i18nInst from "i18next";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  appLanguageAtom,
  appLanguageSelector,
  changeI18nLang,
  getLangLocalStorage,
  profileDtoAtom,
  profileMetadataLanguageSelector,
  resolveAppLanguage,
  setLangLocalStorage,
  SupportedLanguage,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { useProfileActions } from "../auth-profile";

export const useLangSwitch = () => {
  const { updateProfile } = useProfileActions();
  const profileLanguage = useRecoilValue(profileMetadataLanguageSelector);
  const [language, setLanguage] = useRecoilState(appLanguageSelector);
  const setAppLanguage = useSetRecoilState(appLanguageAtom);
  const profile = useRecoilValue(profileDtoAtom);
  const { t } = useTranslation();

  useEffect(() => {
    const init = async (): Promise<void> => {
      // localStorage → profile → current i18n → default. Persist always so
      // logout / profile clear does not drop Hebrew back to English.
      const initialLang = resolveAppLanguage({
        storedLanguage: getLangLocalStorage(),
        preferenceLanguage: profileLanguage,
        currentLanguage: i18nInst.language,
      });

      if (i18nInst.language !== initialLang) {
        await changeI18nLang(initialLang);
      }

      document.dir = i18nInst.dir();
      dayjs.locale(initialLang);
      setAppLanguage(initialLang);
      setLangLocalStorage(initialLang);
    };

    init();
  }, [profileLanguage, setAppLanguage]);

  const switchLangUpdateProfile = useEvent(
    async (lang: SupportedLanguage): Promise<void> => {
      if (lang && lang !== language) {
        setLanguage(lang);
        dayjs.locale(lang);
        const result = await updateProfile({
          metadata: {
            language: lang,
            dateFormat: profile?.metadata?.dateFormat,
            timeFormat: profile?.metadata?.timeFormat,
          },
        });

        if (!result.success) {
          warnAndToastSimpleResult(result, t);
        }
      }
    },
  );

  return {
    switchLangUpdateProfile,
  };
};
